import { useCallback, useRef, useState } from "react"
import { objectForSstId } from "shared/reservationSyncData"
import {
  ReservationSyncDto,
  ReservationSyncObjectDto,
  useLazyGetReservationSyncObjectUrlQuery,
} from "src/features/things/reservationSyncApi"
import { describeQueryError } from "../../features/baseQuerySl2"
import {
  createSnapshotPartCache,
  type SnapshotPartCache,
} from "./snapshotPartCache"

export type ReportKind = "drift" | "sync" | "revert" | "snapshots"

export type LoadedReport<T> = {
  rows: T[]
  /** Parts fetched so far, and how many the manifest lists. */
  partsLoaded: number
  partsTotal: number
  bytesLoaded: number
  loading: boolean
  error?: string
}

const emptyReport = <T,>(partsTotal: number): LoadedReport<T> => ({
  rows: [],
  partsLoaded: 0,
  partsTotal,
  bytesLoaded: 0,
  loading: false,
})

/**
 * Load a stored report ONE OBJECT AT A TIME (LOCKER-374 D16/D21).
 *
 * Reports are split at a few megabytes per object, and a big locker's drift report
 * is several parts. Fetching them all up front would block the tab on the whole
 * report; fetching them on demand lets the first part render while the rest waits
 * for the reader to ask. `partsLoaded` / `partsTotal` is what the tab shows so
 * nobody mistakes a first page for the whole report.
 *
 * Each fetch is two hops by design: the API hands back a short-lived presigned URL
 * (validated against the run's own manifest), then the browser reads S3 directly.
 * That keeps a multi-megabyte body out of API Gateway, whose response limit a real
 * report would exceed.
 */
export const useReservationSyncReport = <T,>(run?: ReservationSyncDto) => {
  const [fetchUrl] = useLazyGetReservationSyncObjectUrlQuery()
  const [reports, setReports] = useState<Record<string, LoadedReport<T>>>({})

  const objectsOf = useCallback(
    (kind: ReportKind): ReservationSyncObjectDto[] =>
      (run?.objects ?? [])
        .filter((o) => o.kind === kind)
        .sort((a, b) => a.rowFrom - b.rowFrom),
    [run],
  )

  const fetchObject = useCallback(
    async (object: ReservationSyncObjectDto): Promise<unknown[]> => {
      if (!run) return []
      const res = await fetchUrl({
        runId: run.runId,
        tenantId: run.tenantId,
        key: object.key,
      }).unwrap()
      const body = await fetch(res.data.url)
      if (!body.ok)
        throw new Error(
          `S3 returned ${body.status} for ${object.key} — the presigned URL may have expired`,
        )
      const parsed = (await body.json()) as { rows?: unknown[] }
      return parsed.rows ?? []
    },
    [fetchUrl, run],
  )

  /** Load the next unloaded part of a kind. Idempotent while a fetch is in flight. */
  const loadNextPart = useCallback(
    async (kind: ReportKind) => {
      const objects = objectsOf(kind)
      const current = reports[kind] ?? emptyReport<T>(objects.length)
      if (current.loading || current.partsLoaded >= objects.length) return
      const next = objects[current.partsLoaded]

      setReports((r) => ({
        ...r,
        [kind]: { ...current, partsTotal: objects.length, loading: true, error: undefined },
      }))
      try {
        const rows = (await fetchObject(next)) as T[]
        setReports((r) => {
          const prev = r[kind] ?? emptyReport<T>(objects.length)
          return {
            ...r,
            [kind]: {
              rows: [...prev.rows, ...rows],
              partsLoaded: prev.partsLoaded + 1,
              partsTotal: objects.length,
              bytesLoaded: prev.bytesLoaded + next.bytes,
              loading: false,
            },
          }
        })
      } catch (error: any) {
        setReports((r) => ({
          ...r,
          [kind]: {
            ...(r[kind] ?? emptyReport<T>(objects.length)),
            loading: false,
            error: describeQueryError(error),
          },
        }))
      }
    },
    [fetchObject, objectsOf, reports],
  )

  const reportOf = useCallback(
    (kind: ReportKind): LoadedReport<T> =>
      reports[kind] ?? emptyReport<T>(objectsOf(kind).length),
    [objectsOf, reports],
  )

  return { reportOf, loadNextPart, objectsOf }
}

export type SnapshotPairDto = {
  sstId: string
  partner: Record<string, unknown> | null
  ours: Record<string, unknown> | null
}

/**
 * Fetch the FULL snapshot pair for ONE reservation, and nothing else.
 *
 * Resolves the row to the single snapshot object whose id range covers it, using
 * the same `objectForSstId` the scripts used to write those ranges. This is the
 * whole reason snapshots live in their own id-ordered objects: opening one row
 * costs one part, not the 64 MB a real locker's snapshots weigh.
 *
 * A part is fetched AT MOST ONCE per run, however many of its rows are opened —
 * see `snapshotPartCache`. Rows in a part already held cost no request at all,
 * which is what makes the id-range split worth having: without it, three rows of
 * one part meant three downloads of the same multi-megabyte object.
 *
 * The consequence, said out loud: the server-side admin log now records one read
 * per PART, not one per row. That is the honest granularity — the presigned URL
 * hands over the whole part on the first click, so the operator already held every
 * row in it. A second log line for row 2 was never evidence they read row 2.
 */
export const useSnapshotPair = (run?: ReservationSyncDto) => {
  const [fetchUrl] = useLazyGetReservationSyncObjectUrlQuery()
  const [state, setState] = useState<
    Record<
      string,
      {
        loading: boolean
        pair?: SnapshotPairDto
        error?: string
        fromKey?: string
        /** Served from a part already held — no request was made for this row. */
        cached?: boolean
      }
    >
  >({})

  // Keyed by run: navigating to another run must not serve its rows out of the
  // previous run's parts, and the parts are large enough that holding both is
  // wasteful even when the keys happen not to collide.
  const cacheRef = useRef<{
    runId?: string
    cache?: SnapshotPartCache<SnapshotPairDto>
  }>({})

  const partCacheFor = useCallback(
    (target: ReservationSyncDto) => {
      if (cacheRef.current.runId !== target.runId || !cacheRef.current.cache) {
        cacheRef.current = {
          runId: target.runId,
          cache: createSnapshotPartCache<SnapshotPairDto>(async (key) => {
            const res = await fetchUrl({
              runId: target.runId,
              tenantId: target.tenantId,
              key,
            }).unwrap()
            const body = await fetch(res.data.url)
            if (!body.ok) throw new Error(`S3 returned ${body.status}`)
            const parsed = (await body.json()) as { rows?: SnapshotPairDto[] }
            return parsed.rows ?? []
          }),
        }
      }
      return cacheRef.current.cache!
    },
    [fetchUrl],
  )

  const load = useCallback(
    async (sstId: string) => {
      if (!run || state[sstId]?.loading || state[sstId]?.pair) return
      const object = objectForSstId(
        run.objects.filter((o) => o.kind === "snapshots"),
        sstId,
      )
      if (!object) {
        setState((s) => ({
          ...s,
          [sstId]: {
            loading: false,
            error:
              "no snapshot object covers this reservation — the run may have been stored with --no-snapshots, or its objects have expired",
          },
        }))
        return
      }
      const cache = partCacheFor(run)
      const alreadyHeld = cache.isLoaded(object.key)
      // No spinner for a part already in hand: the row resolves in this tick, and
      // flashing "fetching…" for it would misreport a request that never happens.
      if (!alreadyHeld) setState((s) => ({ ...s, [sstId]: { loading: true } }))
      try {
        const rows = await cache.rowsFor(object.key)
        const pair = rows.find((p) => p.sstId === sstId)
        setState((s) => ({
          ...s,
          [sstId]: pair
            ? { loading: false, pair, fromKey: object.key, cached: alreadyHeld }
            : {
                loading: false,
                error: `${object.key} does not contain ${sstId} — the manifest's id range disagrees with the object`,
              },
        }))
      } catch (error: any) {
        setState((s) => ({
          ...s,
          [sstId]: { loading: false, error: describeQueryError(error) },
        }))
      }
    },
    [partCacheFor, run, state],
  )

  return { snapshotState: state, loadSnapshot: load }
}
